<script setup lang="ts">
import type { CalendarRange } from './build-calendar-range-filter';
import type { DensityLevel } from './calendar-density';
import type { CalendarEventSpan } from './calendar-events';
import { Temporal } from 'temporal-polyfill';
import { computed, nextTick, onMounted, useId, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { getDateTimeFormat, getMonthWeeks, getWeekdayDates, toNativeDate, useUserLocale } from '#layers/base/public';
import { CALENDAR_DAY_LABEL_FORMAT } from '#layers/listing/constants/calendar';
import { getDensityLevel, getDensityLevels } from './calendar-density';
import { assignEventsToDays } from './calendar-events';
import { useYearGridNavigation } from './use-year-grid-navigation';

const {
	range,
	focusedDate,
	events,
	today,
	weekStartsOn,
} = defineProps<{
	range: CalendarRange;
	focusedDate: Temporal.PlainDate;
	events: readonly CalendarEventSpan[];
	today: Temporal.PlainDate;
	weekStartsOn: 1 | 7;
}>();

const emit = defineEmits<{
	selectDay: [date: Temporal.PlainDate];
	selectMonth: [month: Temporal.PlainYearMonth];
}>();

interface DayCell {
	date: Temporal.PlainDate;
	key: string;
	label: string;
	toneClass: string;
	isToday: boolean;
}

interface MonthModel {
	key: string;
	month: Temporal.PlainYearMonth;
	title: string;
	isCurrent: boolean;
	weeks: (DayCell | null)[][];
}

const { t } = useI18n();
const { formatLocale } = useUserLocale();
const id = useId();

const eventsByDay = computed(() => assignEventsToDays({ events, range }));

const densityLevels = computed(() => getDensityLevels(Array.from(eventsByDay.value.values(), (dayEvents) => dayEvents.length)));

const legendLevels = computed(() => densityLevels.value.map((level, index) => ({
	...level,
	label: index === 0 ? formatCount(level.minCount) : t('layout.calendar.density_at_least', { count: formatCount(level.minCount) }),
})));

const months = computed<MonthModel[]>(() => listMonths(range).map((month) => toMonthModel(month)));

const weekdays = computed(() => getWeekdayDates(weekStartsOn).map((date) => ({
	label: date.toLocaleString(formatLocale.value, { weekday: 'narrow' }),
	name: date.toLocaleString(formatLocale.value, { weekday: 'long' }),
})));

const scroller = useTemplateRef<HTMLElement>('scroller');

const { activeKey, onGridKeydown } = useYearGridNavigation({
	root: scroller,
	range: () => range,
	focusedDate: () => focusedDate,
	weekStartsOn: () => weekStartsOn,
});

function listMonths({ start, end }: CalendarRange): Temporal.PlainYearMonth[] {
	const months: Temporal.PlainYearMonth[] = [];
	const last = end.toPlainYearMonth();

	for (let month = start.toPlainYearMonth(); Temporal.PlainYearMonth.compare(month, last) <= 0; month = month.add({ months: 1 })) {
		months.push(month);
	}

	return months;
}

function toMonthModel(month: Temporal.PlainYearMonth): MonthModel {
	return {
		key: month.toString(),
		month,
		// Formats through a PlainDate, since PlainYearMonth.toLocaleString throws under native Temporal.
		title: month.toPlainDate({ day: 1 }).toLocaleString(formatLocale.value, { month: 'long' }),
		isCurrent: month.equals(today.toPlainYearMonth()),
		weeks: getMonthWeeks({ month, weekStartsOn }).map((week) => week.map((date) => date && toDayCell(date))),
	};
}

function toDayCell(date: Temporal.PlainDate): DayCell {
	const key = date.toString();
	const formattedDate = getDateTimeFormat(formatLocale.value, { ...CALENDAR_DAY_LABEL_FORMAT, timeZone: 'UTC' }).format(toNativeDate(date.toZonedDateTime('UTC')));
	const count = eventsByDay.value.get(key)?.length ?? 0;

	return {
		date,
		key,
		label: t('layout.calendar.day_label', { date: formattedDate, count }, count),
		toneClass: getToneClass(date, getDensityLevel(densityLevels.value, count)),
		isToday: date.equals(today),
	};
}

function getToneClass(date: Temporal.PlainDate, level: DensityLevel | undefined) {
	if (level) return `${level.fillClass} text-highlighted`;
	return isWeekend(date) ? 'text-dimmed' : 'text-muted';
}

function isWeekend(date: Temporal.PlainDate) {
	return date.dayOfWeek >= 6;
}

function formatCount(count: number) {
	return count.toLocaleString(formatLocale.value);
}

// Scrolls only the scroller, since scrollIntoView would also move the page around it.
async function scrollToDate(date: Temporal.PlainDate) {
	await nextTick();

	const container = scroller.value;
	const month = container?.querySelector<HTMLElement>(`[data-month="${date.toPlainYearMonth().toString()}"]`);
	if (!container || !month) return;

	const top = month.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
	container.scrollTo({ top: top - (container.clientHeight - month.offsetHeight) / 2, behavior: 'instant' });
}

onMounted(() => scrollToDate(focusedDate));

defineExpose({ scrollToDate });
</script>

<template>
	<div
		ref="scroller"
		class="@container flex-1 overflow-y-auto"
	>
		<!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- delegates arrow-key movement between the day buttons, which are the interactive elements -->
		<div
			class="grid grid-cols-1 items-start gap-x-10 gap-y-7 px-6 py-3 @w-xs:grid-cols-2 @w-md:grid-cols-4"
			@keydown="onGridKeydown"
		>
			<section
				v-for="month in months"
				:key="month.key"
				:data-month="month.key"
				class="w-full max-w-md"
			>
				<!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -- the DButton inside renders the month name -->
				<h3
					:id="`${id}-${month.key}`"
					class="mb-1.5 flex"
				>
					<DButton
						variant="link"
						:color="month.isCurrent ? 'primary' : 'neutral'"
						:label="month.title"
						size="sm"
						class="p-0"
						@click="emit('selectMonth', month.month)"
					/>
				</h3>

				<div
					role="grid"
					:aria-labelledby="`${id}-${month.key}`"
				>
					<div
						role="row"
						class="grid grid-cols-7"
					>
						<span
							v-for="weekday in weekdays"
							:key="weekday.name"
							role="columnheader"
							:aria-label="weekday.name"
							class="py-0.5 text-center text-2xs text-dimmed"
						>
							{{ weekday.label }}
						</span>
					</div>

					<div
						v-for="(week, weekIndex) in month.weeks"
						:key="weekIndex"
						role="row"
						class="grid grid-cols-7"
					>
						<div
							v-for="(day, dayIndex) in week"
							:key="day?.key ?? dayIndex"
							role="gridcell"
							class="h-6.5"
						>
							<button
								v-if="day"
								type="button"
								:data-date="day.key"
								:aria-label="day.label"
								:aria-current="day.isToday ? 'date' : undefined"
								:tabindex="day.key === activeKey ? 0 : -1"
								class="group flex size-full items-center justify-center rounded-sm p-0.5 focus:outline-none"
								@click="emit('selectDay', day.date)"
							>
								<span
									class="
										flex size-full max-w-9 items-center justify-center rounded-sm text-2xs tabular-nums
										group-hover:shadow-(--cal-day-hover)
										group-focus-visible:outline-2 group-focus-visible:outline-offset-1 group-focus-visible:outline-primary
									"
									:class="day.isToday ? 'bg-primary font-semibold text-inverted' : day.toneClass"
								>
									{{ day.date.day }}
								</span>
							</button>
						</div>
					</div>
				</div>
			</section>
		</div>

		<div
			v-if="legendLevels.length > 0"
			class="flex items-center justify-end gap-2.5 px-6 pt-2 pb-5 text-2xs text-muted"
		>
			<span :id="`${id}-legend`">{{ $t('layout.calendar.density_legend') }}</span>
			<ul
				class="flex items-center gap-2.5"
				:aria-labelledby="`${id}-legend`"
			>
				<li
					v-for="level in legendLevels"
					:key="level.minCount"
					class="flex items-center gap-1 tabular-nums"
				>
					<span
						class="size-3 rounded-xs"
						:class="level.fillClass"
						aria-hidden="true"
					/>
					{{ level.label }}
				</li>
			</ul>
		</div>
	</div>
</template>
